import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Suspense, lazy } from 'react';
import { AuthProvider, useAuth } from '@/hooks/use-auth';
import { AppShell } from '@/components/AppShell';

// ─── Lazy-loaded pages (code-split per route) ─────────────────────────────────
// LoginPage and NotFoundPage are kept eager — they are tiny and always needed.
import LoginPage from '@/pages/LoginPage';
import NotFoundPage from '@/pages/NotFoundPage';

const DashboardPage           = lazy(() => import('@/pages/DashboardPage'));
const UserManagementPage      = lazy(() => import('@/pages/UserManagementPage'));
const UserDetailPage          = lazy(() => import('@/pages/UserDetailPage'));
const ClassroomManagementPage = lazy(() => import('@/pages/ClassroomManagementPage'));
const ClassroomDetailPage     = lazy(() => import('@/pages/ClassroomDetailPage'));
const ContentModerationPage   = lazy(() => import('@/pages/ContentModerationPage'));
const ContentDetailPage       = lazy(() => import('@/pages/ContentDetailPage'));
const AnalyticsDashboardPage  = lazy(() => import('@/pages/AnalyticsDashboardPage'));
const BillingManagementPage   = lazy(() => import('@/pages/BillingManagementPage'));
const SubscriptionDetailPage  = lazy(() => import('@/pages/SubscriptionDetailPage'));
const AITutorOversightPage    = lazy(() => import('@/pages/AITutorOversightPage'));
const AISessionDetailPage     = lazy(() => import('@/pages/AISessionDetailPage'));
const TranslationsPage        = lazy(() => import('@/pages/TranslationsPage'));
const TranslationKeyDetailPage = lazy(() => import('@/pages/TranslationKeyDetailPage'));
const SystemConfigPage        = lazy(() => import('@/pages/SystemConfigPage'));
const NotificationsPage       = lazy(() => import('@/pages/NotificationsPage'));
const AdminAccountsPage       = lazy(() => import('@/pages/AdminAccountsPage'));
const ImpersonationPage       = lazy(() => import('@/pages/ImpersonationPage'));
const AcceptInvitePage        = lazy(() => import('@/pages/AcceptInvitePage'));

// ─── Query client ─────────────────────────────────────────────────────────────

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

// ─── Page loading fallback ────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex h-full items-center justify-center p-12">
      <div className="text-sm text-gray-400">Loading…</div>
    </div>
  );
}

// ─── Route guard ─────────────────────────────────────────────────────────────

// Maps each protected route to the minimum permission required to view it.
// Any role lacking this permission is redirected to the dashboard with a
// query param so the shell can display a "Permission denied" toast.
const ROUTE_PERMISSION_MAP: Record<string, string> = {
  '/analytics':      'analytics.view',
  '/billing':        'billing.view',
  '/ai':             'ai.view',
  '/notifications':  'notifications.view',
  '/system':         'system.monitoring',
  '/admin-accounts': 'admins.view',
  '/impersonation':  'users.impersonate',
  '/translations':   'translations.view',
};

function PermissionRoute({
  path,
  children,
}: {
  path: string;
  children: React.ReactNode;
}) {
  const { session } = useAuth();
  const required = ROUTE_PERMISSION_MAP[path];

  if (required && session && !(session.permissions as string[])?.includes(required)) {
    return <Navigate to={`/?denied=${encodeURIComponent(required)}`} replace />;
  }

  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { session, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-background">
        <div className="text-muted-foreground text-sm">Loading…</div>
      </div>
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  return <AppShell>{children}</AppShell>;
}

// ─── Routes ───────────────────────────────────────────────────────────────────

function AppRoutes() {
  const { session } = useAuth();

  return (
    <Suspense fallback={<PageLoader />}>
      <Routes>
        <Route
          path="/login"
          element={session ? <Navigate to="/" replace /> : <LoginPage />}
        />
        <Route
          path="/"
          element={<ProtectedRoute><DashboardPage /></ProtectedRoute>}
        />
        <Route
          path="/users"
          element={<ProtectedRoute><UserManagementPage /></ProtectedRoute>}
        />
        <Route
          path="/users/:id"
          element={<ProtectedRoute><UserDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/classrooms"
          element={<ProtectedRoute><ClassroomManagementPage /></ProtectedRoute>}
        />
        <Route
          path="/classrooms/:id"
          element={<ProtectedRoute><ClassroomDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/content"
          element={<ProtectedRoute><ContentModerationPage /></ProtectedRoute>}
        />
        <Route
          path="/content/:id"
          element={<ProtectedRoute><ContentDetailPage /></ProtectedRoute>}
        />
        <Route
          path="/analytics"
          element={<ProtectedRoute><PermissionRoute path="/analytics"><AnalyticsDashboardPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/billing"
          element={<ProtectedRoute><PermissionRoute path="/billing"><BillingManagementPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/billing/:id"
          element={<ProtectedRoute><PermissionRoute path="/billing"><SubscriptionDetailPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/ai"
          element={<ProtectedRoute><PermissionRoute path="/ai"><AITutorOversightPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/ai/sessions/:id"
          element={<ProtectedRoute><PermissionRoute path="/ai"><AISessionDetailPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/translations"
          element={<ProtectedRoute><PermissionRoute path="/translations"><TranslationsPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/translations/keys/:id"
          element={<ProtectedRoute><PermissionRoute path="/translations"><TranslationKeyDetailPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/system"
          element={<ProtectedRoute><PermissionRoute path="/system"><SystemConfigPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/notifications"
          element={<ProtectedRoute><PermissionRoute path="/notifications"><NotificationsPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/admin-accounts"
          element={<ProtectedRoute><PermissionRoute path="/admin-accounts"><AdminAccountsPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/impersonation"
          element={<ProtectedRoute><PermissionRoute path="/impersonation"><ImpersonationPage /></PermissionRoute></ProtectedRoute>}
        />
        <Route
          path="/accept-invite"
          element={<AcceptInvitePage />}
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
